import { getSessionStorage } from '@/shared/lib/storage';
import { isDebugEnabled } from '@/shared/lib/system';
import type { FeatureFlags, FeatureFlagType, FeatureStatus } from './featureFlag';
import { useSessionStore } from './sessionStore';

const FEATURE_STATUS_VALUES: readonly FeatureStatus[] = ['visible', 'disabled', 'hidden'];

const isFeatureStatus = (value: unknown): value is FeatureStatus =>
  typeof value === 'string' && FEATURE_STATUS_VALUES.includes(value as FeatureStatus);

/**
 * 機能フラグ（テナント単位）のステータスを取得する（React hook 版）
 *
 * 解決順: デバッグオーバーライド → API取得値 → デフォルト('hidden')
 * 未定義のフラグは安全側に倒して `hidden`（非表示・利用不可扱い）。
 */
export const useFeatureFlag = (featureFlagType: FeatureFlagType): FeatureStatus => {
  const features = useSessionStore((state) => state.features);
  return resolveFeatureFlag({ featureFlagType, features });
};

/**
 * 機能フラグ（テナント単位）のステータスを取得する（非hook版）
 *
 * `beforeLoad` 等の非コンポーネントコンテキストから使用する。
 * 解決順は hook 版と同一（デバッグオーバーライド → API取得値 → デフォルト('hidden')）。
 */
export const getFeatureFlagStatus = (featureFlagType: FeatureFlagType): FeatureStatus => {
  const features = useSessionStore.getState().features;
  return resolveFeatureFlag({ featureFlagType, features });
};

/**
 * 機能フラグのステータスを解決する内部関数
 *
 * デバッグオーバーライド (dev/local のみ) → API取得値 → デフォルト('hidden')
 */
const resolveFeatureFlag = ({
  featureFlagType,
  features,
}: {
  featureFlagType: FeatureFlagType;
  features: FeatureFlags;
}): FeatureStatus => {
  if (isDebugEnabled()) {
    const overrides = getSessionStorage('debug-feature-flags');
    const override = overrides?.[featureFlagType];
    if (isFeatureStatus(override)) {
      return override;
    }
  }

  return features[featureFlagType] ?? 'hidden';
};
